// src/pages/EnterNamePage.tsx - Complete with BatchID localStorage setup
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ArrowRight, History } from 'lucide-react';
import toast from 'react-hot-toast';

const EnterNamePage: React.FC = () => {
  const navigate = useNavigate();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  // Auto focus on mount
  useEffect(() => {
    const focusInput = () => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    };

    // Multiple focus attempts for production
    focusInput();
    setTimeout(focusInput, 100);
    setTimeout(focusInput, 300);
    requestAnimationFrame(focusInput);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    console.log('Form submitted with name:', trimmedName);
    
    if (!trimmedName) {
      setError('Please enter your name');
      toast.error('Please enter your name');
      return;
    }

    if (trimmedName.length < 1) {
      setError('Name must be at least 1 character');
      toast.error('Name must be at least 1 character');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Name must be less than 50 characters');
      toast.error('Name must be less than 50 characters');
      return;
    }

    // Clear any errors
    setError('');

    // Generate session data with readable date format
    const now = new Date();
    const sessionTimestamp = now.toISOString();
    
    // Create readable date format: YYYYMMDD_HHMMSS_milliseconds
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    
    const readableDateTime = `${year}${month}${day}_${hours}${minutes}${seconds}_${milliseconds}`;
    
    // Generate batch ID: CleanedName_ReadableDateTime
    const cleanName = trimmedName.replace(/[^a-zA-Z0-9]/g, '');
    const batchId = `${cleanName}_${readableDateTime}`;

    // Store only batch ID - contains name and readable timestamp
    localStorage.setItem('scannerBatchId', batchId);
    
    // Log for debugging
    console.log('✅ Session data stored in localStorage:');
    console.log('- scannerBatchId:', localStorage.getItem('scannerBatchId'));
    
    toast.success(`Welcome ${trimmedName}! Session started.`);
    navigate('/scan-tag');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (error) {
      setError('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* History Button - Top Right */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate('/history')}
            className="inline-flex items-center px-5 py-3 text-sm font-medium text-indigo-600 bg-white rounded-lg shadow-sm border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <History className="h-4 w-4 mr-2" />
            View History
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white shadow-xl rounded-2xl p-10">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="bg-indigo-100 p-4 rounded-full inline-block mb-6">
              <User className="h-10 w-10 text-indigo-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-3">Welcome</h1>
            <p className="text-lg text-gray-600">Please enter your name to begin scanning</p>
          </div>

          {/* Name Input Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label htmlFor="name" className="block text-base font-semibold text-gray-700 mb-3">
                Your Name
              </label>
              <div className="relative">
                <input
                  ref={nameInputRef}
                  id="name"
                  name="name"
                  type="text"
                  value={name}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your name..."
                  autoFocus
                  autoComplete="name"
                  className={`w-full px-6 py-4 pl-14 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-xl ${
                    error ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                <User className="absolute left-5 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              </div>
              {error && (
                <p className="mt-3 text-sm text-red-600">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!name.trim()}
              className={`w-full flex items-center justify-center py-4 px-8 rounded-xl font-semibold text-xl transition-all transform ${
                !name.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 hover:scale-105 shadow-lg hover:shadow-xl'
              }`}
            >
              Start Scanning
              <ArrowRight className="ml-3 h-6 w-6" />
            </button>
          </form>

          {/* Secondary Actions */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/history')}
                className="inline-flex items-center text-base text-gray-600 hover:text-indigo-600 transition-colors duration-200"
              >
                <History className="h-5 w-5 mr-2" />
                Or view previous scanning sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterNamePage;